export * from "./word";

import { padLeft } from "mithril-materialized";
import type { Page, Pages } from "../models";
import {
  getDefaultCapabilityModelItem,
  type ICapabilityDataModel,
  type ILabelled,
} from "../models/capability-model/capability-model";
import { tDynamic } from "../services/translations";

export const LANGUAGE = "SG_LANGUAGE";
export const SAVED = "SG_MODEL_SAVED";

const supRegex = /\^([^_ ]+)(_|$|\s)/g;
const subRegex = /\_([^\^ ]+)(\^|$|\s)/g;

/** Expand markdown notation by converting A_1 to subscript and x^2 to superscript. */
export const subSup = (s: string) =>
  s
    ? s.replace(supRegex, `<sup>$1</sup>`).replace(subRegex, `<sub>$1</sub>`)
    : s;

export const capitalize = (s?: string) =>
  s && s.charAt(0).toUpperCase() + s.slice(1);

const stringifyTranslation = (value: unknown) =>
  Array.isArray(value) ? value.join("") : value == null ? "" : `${value}`;

export const hasTranslation = (translated: unknown, key: string) => {
  const text = stringifyTranslation(translated);
  return !!text && text !== key && text !== `@@${key}@@`;
};

export const translatedOrFallback = (
  translated: unknown,
  key: string,
  fallback: string,
) =>
  hasTranslation(translated, key) ? stringifyTranslation(translated) : fallback;

export const translateLabelOrFallback = <
  T extends { id?: string; label: string },
>(
  item: T,
) => {
  if (!item.id) return item.label;

  const defaultItem = getDefaultCapabilityModelItem(item.id);
  if (!defaultItem || item.label !== defaultItem.label) return item.label;

  return translatedOrFallback(tDynamic(item.id), item.id, item.label);
};

/**
 * Debounce function wrapper, i.e. between consecutive calls of the wrapped function,
 * there will be at least TIMEOUT milliseconds.
 * @param func Function to execute
 * @param timeout Timeout in milliseconds
 * @returns
 */
export const debounce = <T extends (...args: unknown[]) => void>(
  func: T,
  timeout: number,
) => {
  let timer: number;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      func(...args);
    }, timeout);
  };
};

/**
 * Format date to YYYY-MM-DD string.
 * @param date Date, number (timestamp), or string (ISO format). Defaults to current date if undefined.
 * @returns Formatted date string or empty string for invalid dates
 */
export const formatDate = (date?: number | Date | string): string => {
  if (date === undefined) {
    date = Date.now();
  }
  const d = new Date(date);
  if (isNaN(d.getTime())) {
    return "";
  }
  return `${d.getFullYear()}-${padLeft(d.getMonth() + 1)}-${padLeft(d.getDate())}`;
};

/**
 * Get a color that is clearly visible against a background color
 * @param backgroundColor Background color, e.g. #99AABB
 * @returns
 */
export const getTextColorFromBackground = (backgroundColor?: string) => {
  if (!backgroundColor) {
    return "black-text";
  }
  const c = backgroundColor.substring(1); // strip #
  const rgb = parseInt(c, 16); // convert rrggbb to decimal
  const r = (rgb >> 16) & 0xff; // extract red
  const g = (rgb >> 8) & 0xff; // extract green
  const b = (rgb >> 0) & 0xff; // extract blue

  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

  return luma < 105 ? "white-text" : "black-text";
};

type Option<T> = {
  id: T;
  label: string;
  title?: string;
};

export const getOptionsLabel = <T>(
  options: Array<Option<T>>,
  id?: T | T[],
  showTitle = true,
) => {
  if (!id) {
    return "";
  }
  const print = (o: Option<T>) =>
    showTitle
      ? `${o.label}${o.title ? ` (${o.title.replace(/\.\s*$/, "")})` : ""}`
      : o.label;
  if (id instanceof Array) {
    return options
      .filter((o) => id.indexOf(o.id) >= 0)
      .map((o) => print(o))
      .join(", ");
  }
  const found = options.filter((o) => o.id === id).shift();
  return found ? print(found) : "";
};

/** Join a list of items with a comma, and use AND for the last item in the list. */
export const joinListWithAnd = (
  arr: string[] = [],
  and = "and",
  prefix = "",
) => {
  const terms = arr.filter((term) => term);
  return terms.length === 0
    ? ""
    : prefix +
    (terms.length === 1
      ? terms[0]
      : `${terms
        .slice(0, terms.length - 1)
        .map((t, i) =>
          i === 0 || typeof t === "undefined" ? t : t.toLowerCase(),
        )
        .join(", ")} ${and} ${terms[terms.length - 1].toLowerCase()}`);
};

/** Convert markdown text to HTML */
// export const markdown2html = (markdown = '') =>
//   m.trust(render(markdown, true, true));

export const isUnique = <T>(item: T, pos: number, arr: T[]) =>
  arr.indexOf(item) == pos;

/** Generate an array of numbers, from start till end, with optional step size. */
export const generateNumbers = (
  start: number,
  end: number,
  step: number = 1,
): number[] => {
  if (start > end) {
    throw new Error(
      "Start number must be less than or equal to the end number.",
    );
  }

  if (step <= 0) {
    throw new Error("Step size must be a positive number.");
  }

  const length = Math.floor((end - start) / step) + 1;
  return Array.from({ length }, (_, index) => start + index * step);
};

export const getRandomValue = <T>(array: T[]): T | undefined => {
  if (array.length === 0) {
    return undefined;
  }

  const randomIndex = Math.floor(Math.random() * array.length);
  return array[randomIndex];
};

/**
 * Deep copy function for TypeScript.
 * @param T Generic type of target/copied value.
 * @param target Target value to be copied.
 * @see Source project, ts-deepcopy https://github.com/ykdr2017/ts-deepcopy
 * @see Code pen https://codepen.io/ErikVullings/pen/ejyBYg
 */
export const deepCopy = <T>(target: T): T => {
  if (target === null) {
    return target;
  }
  if (target instanceof Date) {
    return new Date(target.getTime()) as any;
  }
  if (target instanceof Array) {
    const cp = [] as any[];
    (target as any[]).forEach((v) => {
      cp.push(v);
    });
    return cp.map((n: any) => deepCopy<any>(n)) as any;
  }
  if (typeof target === "object") {
    const cp = { ...(target as { [key: string]: any }) } as {
      [key: string]: any;
    };
    Object.keys(cp).forEach((k) => {
      cp[k] = deepCopy<any>(cp[k]);
    });
    return cp as T;
  }
  return target;
};

/** Compute a contrasting background color */
export const contrastingColor = (backgroundColor: string) => {
  const backgroundRgb = [
    parseInt(backgroundColor[1] + backgroundColor[2], 16),
    parseInt(backgroundColor[3] + backgroundColor[4], 16),
    parseInt(backgroundColor[5] + backgroundColor[6], 16),
  ];
  const luminance =
    0.2126 * backgroundRgb[0] +
    0.7152 * backgroundRgb[1] +
    0.0722 * backgroundRgb[2];

  // If the background is dark, use white text.
  if (luminance < 20) {
    return "#ffffff";
  }

  // If the background is light, use black text.
  return "#000000";
};

export const scrollToSection = (e: MouseEvent, id: string): void => {
  e.preventDefault();
  const element = document.getElementById(id);

  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  } else {
    console.log(`Element with id ${id} not found.`);
  }
};

export const scrollToTop = (): void => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

export const isActivePage = (page: Pages) => (d: Page) =>
  page === d.id ? "active" : undefined;

/**
 * Determines whether the current page is considered small based on the width of the window.
 * @returns A boolean indicating whether the current page is small.
 */
export const isSmallPage = (): boolean => {
  const width = window.innerWidth;

  // Materialize medium size range: 601px - 992px
  return width < 601;
  // && width <= 992;
};

/**
 * Create a GUID
 * @see https://stackoverflow.com/a/2117523/319711
 *
 * @returns RFC4122 version 4 compliant GUID
 */
export const uuid4 = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    // tslint:disable-next-line:no-bitwise
    const r = (Math.random() * 16) | 0;
    // tslint:disable-next-line:no-bitwise
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Create a unique ID
 * @see https://stackoverflow.com/a/2117523/319711
 *
 * @returns RFC4122 version 4 compliant GUID
 */
export const uniqueId = () => {
  return "idxxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    // tslint:disable-next-line:no-bitwise
    const r = (Math.random() * 16) | 0;
    // tslint:disable-next-line:no-bitwise
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const toLetters = (num: number): string => {
  const mod = num % 26;
  // tslint:disable-next-line:no-bitwise
  let pow = (num / 26) | 0;
  const out = mod ? String.fromCharCode(64 + mod) : (--pow, "Z");
  return pow ? toLetters(pow) + out : out;
};

/**
 * Generate a sequence of numbers between from and to with step size: [from, to].
 *
 * @static
 * @param {number} from
 * @param {number} to : inclusive
 * @param {number} [count=to-from+1]
 * @param {number} [step=1]
 * @returns
 */
export const range = (
  from: number,
  to: number,
  count: number = to - from + 1,
  step: number = 1,
) => {
  // See here: http://stackoverflow.com/questions/3746725/create-a-javascript-array-containing-1-n
  // let a = Array.apply(null, {length: n}).map(Function.call, Math.random);
  const a: number[] = new Array(count);
  const min = from;
  const max = to - (count - 1) * step;
  const theRange = max - min;
  const x0 = Math.round(from + theRange * Math.random());
  for (let i = 0; i < count; i++) {
    a[i] = x0 + i * step;
  }
  return a;
};

/**
 * Shuffles array in place. ES6 version
 * @param {Array} a items An array containing the items.
 */
export const shuffle = (a: Array<any>) => {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const unwantedChars = /[\-'_]/g;

export const cleanUpSpecialChars = (str: string) =>
  str
    .replace(/[ÀÁÂÃÄÅàáâãäå]/g, "a")
    .replace(/[ÈÉÊËèéêë]/g, "e")
    .replace(/[ÒÓÔÖòóôö]/g, "o")
    .replace(/[ÌÍÎÏìíîï]/g, "i")
    .replace(/[ÙÚÛÜùúûü]/g, "u")
    .replace(/[ç]/g, "c")
    .replace(/[ș]/g, "s")
    .replace(/[ț]/g, "t")
    .replace(/[\/\-]|&amp;|\s+/g, " ")
    .replace(/[^a-z0-9 ]/gi, ""); // final clean up

/**
 * Join a list of items with a comma.
 * Removes empty items, and optionally adds brackets around the comma separated list.
 */
export const formatOptional = (
  options: { brackets?: boolean; prepend?: string; append?: string },
  ...items: Array<string | number | undefined>
) => {
  const { brackets, prepend = "", append = "" } = options;
  const f = items.filter((i) => typeof i !== "undefined" && i !== "");
  if (!f || f.length === 0) {
    return "";
  }
  const txt = `${prepend}${f.join(", ")}${append}`;
  return f.length === 0 ? "" : brackets ? ` (${txt})` : txt;
};

/**
 * Returns a random integer between min (inclusive) and max (inclusive), optionally filtered.
 * If a filter is supplied, only returns numbers that satisfy the filter.
 *
 * @param {number} min
 * @param {number} max
 * @param {Function} filter numbers that do not satisfy the condition
 */
export const random = (
  min: number,
  max: number,
  f?: (n: number, min?: number, max?: number) => boolean,
): number => {
  const x =
    min >= max ? min : Math.floor(Math.random() * (max - min + 1)) + min;
  return f ? (f(x, min, max) ? x : random(min, max, f)) : x;
};

/**
 * Draw N random (unique) numbers between from and to.
 *
 * @static
 * @param {number} from
 * @param {number} to
 * @param {number} count
 * @param {number[]} [existing]
 * @param {(n: number) => boolean} [filter] Optional filter to filter out the results
 * @returns
 */
export const randomNumbers = (
  from: number,
  to: number,
  count: number,
  existing: number[] = [],
  filter?: (n: number, existing?: number[]) => boolean,
) => {
  if (from === to) {
    return Array(count).fill(to);
  }
  if (from > to || count - 1 > to - from) {
    throw Error("Outside range error");
  }
  const result: number[] = [];
  do {
    const x = random(from, to);
    if (existing.indexOf(x) < 0 && result.indexOf(x) < 0) {
      if (!filter || filter(x, result)) {
        result.push(x);
        count--;
      } else {
        count += result.length;
        result.length = 0;
      }
    }
  } while (count > 0);
  return result;
};

/**
 * Returns n random items from an array
 */
export const randomItems = <T>(arr: T[], count = 5): T[] =>
  randomNumbers(0, arr.length - 1, count).map((i) => arr[i]);

/**
 * Get date of ISO week, i.e. starting on a Monday.
 * @param week week number
 * @param year year
 */
export const getDateOfISOWeek = (week: number, year?: number) => {
  const w = year ? week : week % 100;
  if (!year) {
    year = Math.round((week - w) / 100);
  }
  const simple = new Date(year, 0, 1 + (w - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = simple;
  if (dow <= 4) {
    ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }
  return ISOweekStart;
};

export const getWeekNumber = (date: Date) => {
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.valueOf() - yearStart.valueOf()) / 86400000 + 1) / 7);
};

export const list = (arr: string[] = [], prefix = "") =>
  arr.length === 0
    ? ""
    : prefix +
    (arr.length === 1
      ? arr[0]
      : `${arr.slice(0, arr.length - 1).join(", ")} en ${arr[arr.length - 1]}`);

/** Translate label and description of a model item using its ID as the translation key. Falls back to original values if no translation exists. */
const translateItem = <T extends ILabelled>(item: T): T => {
  const labelKey = item.id;
  const descKey = `${item.id}_desc`;
  const defaultItem = getDefaultCapabilityModelItem(item.id);
  const label =
    defaultItem && item.label === defaultItem.label
      ? translatedOrFallback(tDynamic(labelKey), labelKey, item.label)
      : item.label;
  const desc =
    defaultItem && item.desc === defaultItem.desc
      ? translatedOrFallback(tDynamic(descKey), descKey, item.desc || "")
      : item.desc;
  return {
    ...item,
    label,
    ...(item.desc !== undefined ? { desc } : {}),
  };
};

/**
 * Returns a shallow copy of the capability data model with all ILabelled items
 * translated using their IDs as translation keys. Call this at render time
 * (not at store time) to ensure translations reflect the current locale.
 */
export const localizeCapabilityModelData = (
  data: Partial<ICapabilityDataModel>,
): Partial<ICapabilityDataModel> => ({
  ...data,
  mainTasks: data.mainTasks?.map(translateItem),
  taskScale: data.taskScale?.map(translateItem),
  performanceAspects: data.performanceAspects?.map(translateItem),
  performanceScale: data.performanceScale?.map(translateItem),
  mainGaps: data.mainGaps?.map(translateItem),
  gapScale: data.gapScale?.map(translateItem),
  assessmentScale: data.assessmentScale?.map(translateItem),
  categories: data.categories?.map((cat) => ({
    ...translateItem(cat),
    subcategories: cat.subcategories?.map((sc) => ({
      ...translateItem(sc),
      capabilities: sc.capabilities?.map(translateItem),
    })),
  })),
  stakeholders: data.stakeholders?.map(translateItem),
  stakeholderTypes: data.stakeholderTypes?.map(translateItem),
});

/** Translate an assessment question (for solution assessments) using its ID as the translation key. */
const translateAssessmentQuestion = <
  T extends { id?: string; label: string; value?: string },
>(
  item: T,
): T => {
  if (!item.id) return item;
  const labelT = tDynamic(item.id);
  return {
    ...item,
    label: translatedOrFallback(labelT, item.id, item.label),
  };
};

/** Translate compliance check items using their ID as the translation key. */
const translateComplianceCheck = <
  T extends { id?: string; label: string; value?: string },
>(
  item: T,
): T => {
  if (!item.id) return item;
  const labelT = tDynamic(item.id);
  return {
    ...item,
    label: translatedOrFallback(labelT, item.id, item.label),
  };
};

/**
 * Returns a shallow copy of the solution data with all assessment questions
 * translated using their IDs as translation keys. Call this at render time
 * to ensure translations reflect the current locale.
 */
export const localizeSolutionData = <T>(solution: T): T => {
  if (!solution) return solution;
  return {
    ...solution,
    compliance: (solution as any).compliance?.map(translateComplianceCheck),
    userNeeds: (solution as any).userNeeds?.map(translateAssessmentQuestion),
    operationalNeeds: (solution as any).operationalNeeds?.map(
      translateAssessmentQuestion,
    ),
    organisationalNeeds: (solution as any).organisationalNeeds?.map(
      translateAssessmentQuestion,
    ),
    expectedImpact: (solution as any).expectedImpact?.map(
      translateAssessmentQuestion,
    ),
  } as T;
};

export const DutchFlag =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAUCAYAAACaq43EAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyRpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoTWFjaW50b3NoKSIgeG1wTU06SW5zdGFuY2VJRD0ieG1wLmlpZDpDRkNCMjg2RDE3ODMxMUUyQTcxNDlDNEFCRkNENzc2NiIgeG1wTU06RG9jdW1lbnRJRD0ieG1wLmRpZDpDRkNCMjg2RTE3ODMxMUUyQTcxNDlDNEFCRkNENzc2NiI+IDx4bXBNTTpEZXJpdmVkRnJvbSBzdFJlZjppbnN0YW5jZUlEPSJ4bXAuaWlkOkNGQ0IyODZCMTc4MzExRTJBNzE0OUM0QUJGQ0Q3NzY2IiBzdFJlZjpkb2N1bWVudElEPSJ4bXAuZGlkOkNGQ0IyODZDMTc4MzExRTJBNzE0OUM0QUJGQ0Q3NzY2Ii8+IDwvcmRmOkRlc2NyaXB0aW9uPiA8L3JkZjpSREY+IDwveDp4bXBtZXRhPiA8P3hwYWNrZXQgZW5kPSJyIj8+dLNUtgAAAEBJREFUeNpiXCej8Z9hAAATwwCBUYtHLaYZYPz///8nIM1LZ3s/j8bxqMU0AyxK7j28A2Av72gcj1o8/CwGCDAAa10IGBJ1C8IAAAAASUVORK5CYII=";
export const EnglishFlag =
  "data:image/png;base64,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";

export const colorPalette = [
  "#fbb4ae",
  "#b3cde3",
  "#ccebc5",
  "#decbe4",
  "#fed9a6",
  "#ffffcc",
  "#e5d8bd",
  "#fddaec",
  "#f2f2f2",
];
// const colors = [
//   '#8dd3c7',
//   '#ffffb3',
//   '#bebada',
//   '#fb8072',
//   '#80b1d3',
//   '#fdb462',
//   '#b3de69',
//   '#fccde5',
//   '#d9d9d9',
// ];
